import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { ButtonPrimary } from '@spotify-internal/encore-web';
import { useTranslation } from 'react-i18next';
import { filter, includes, intersection, isEmpty, isUndefined, mergeWith, some } from 'lodash';
import { all as ALL_COUNTRIES } from '@spotify-internal/market';

import {
  CLAIM_TYPE,
  CLAIM_TYPE_OPTIONS,
  CONTENT_TYPE,
  FieldOnChangeEvent,
  FormData,
} from 'containers/ContentReporting/ContentLegalReporting/utils/types';
import {
  getAssetTypeOptions,
  getUrisWithoutASelectedAssociatedContent,
} from 'containers/ContentReporting/ContentLegalReporting/utils/functions/contentType';
import SelectField from 'components/Fields/SelectField/SelectField';
import { USE_LABEL_FROM_TRANSLATION_PREFIX } from 'components/ContentReporting/shared-constants';
import URIField from 'components/ContentReporting/Form/Fields/URIField';
import WarningDialog from 'components/ContentReporting/WarningDialog';
import ImageChooser from 'components/ContentReporting/Form/Fields/ImageChooser/ImageChooser';
import TextareaField from 'components/Fields/TextareaField/TextareaField';
import {
  areUrisValid,
  getURIErrorMessage,
  getUriMaxAmount,
} from 'containers/ContentReporting/ContentLegalReporting/utils/functions/uri';
import { getContentDescriptionLabel } from 'containers/ContentReporting/ContentLegalReporting/utils/functions/contentDescription';
import {
  ReportedContentInputs,
  ReportedContentInputsErrors,
} from 'containers/ContentReporting/ContentLegalReporting/utils/config/steps/reportedContent';
import { URI_FIELDS } from 'containers/ContentReporting/ContentLegalReporting/utils/config/fields';
import { useAssociatedContent } from 'hooks/useAssociatedContent';
import { useEntityType } from 'hooks/useEntityType';
import { splitTextByDelim } from 'components/ContentReporting/validation-utils';
import TextField from 'components/Fields/TextField/TextField';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { UserInfoContext } from 'context/UserInfo/UserInfoProvider';
import { AssociatedContent } from 'proto/generated/inspector_gadget';
import { formatAssociatedContentIntoEntity } from 'utils/functions';
import UserCountryField, { userCountryIsValid } from 'components/ContentReporting/Form/Fields/UserCountryField';
import { AssetType, assetTypeFromJSON } from 'proto/generated/spotify/catalogmanagement/entity/v1/asset';

import BackButton from '../BackButton/BackButton';
import analytics, { ShowStepEventLabel } from '../report-content-analytics-utils';
import { CONTENT_DETAILS, withFullTranslationKey } from '../content-legal-reporting-config';

type Step2ReportedContentProps = {
  preFilledContentUri?: string;
  formData: FormData;
  goToNextStep: (requiredFields: string[], newFormValues: FormData) => void;
  handleBackButton: () => void;
  renderErrorMessage: () => void;
};

export const getPayloadFields = (_fieldValue: any, _market: string) => [
  CONTENT_DETAILS.fields.CLAIM_TYPE,
  CONTENT_DETAILS.fields.REPORTED_CONTENT_TYPE,
  CONTENT_DETAILS.fields.COMMON_ASSET_TYPE,
  CONTENT_DETAILS.fields.REPORTED_CONTENT_DESCRIPTION,
  CONTENT_DETAILS.fields.ENTITIES,
  CONTENT_DETAILS.fields.COUNTRY,
  CONTENT_DETAILS.fields.RELEVANT_LAW,
];

const TRANSLATION_PREFIX = 'infringement_claim_form';

export const Step2ReportedContent: FunctionComponent<Step2ReportedContentProps> = props => {
  const { formData, goToNextStep, handleBackButton, renderErrorMessage, preFilledContentUri } = props;
  const { userCountryIsoCode } = useContext(UserInfoContext);

  const formDataToStep2Values = {
    claimType: formData[CONTENT_DETAILS.fields.CLAIM_TYPE]?.value,
    contentType: formData[CONTENT_DETAILS.fields.REPORTED_CONTENT_TYPE]?.value,
    commonAssetType: formData[CONTENT_DETAILS.fields.COMMON_ASSET_TYPE]?.value,
    uris: formData[CONTENT_DETAILS.fields.REPORTED_CONTENT_URIS]?.value,
    entities: formData[CONTENT_DETAILS.fields.ENTITIES]?.value,
    assetIds: formData[CONTENT_DETAILS.fields.ASSOCIATED_CONTENT]?.value,
    description: formData[CONTENT_DETAILS.fields.REPORTED_CONTENT_DESCRIPTION]?.value,
    country: formData[CONTENT_DETAILS.fields.COUNTRY]?.value,
    relevantLaw: formData[CONTENT_DETAILS.fields.RELEVANT_LAW]?.value,
  };

  const defaultStep2Values = {
    claimType: undefined,
    contentType: undefined,
    commonAssetType: undefined,
    uris: !!preFilledContentUri ? splitTextByDelim(preFilledContentUri) : [],
    entities: [],
    assetIds: [],
    description: '',
    country: undefined,
    relevantLaw: undefined,
  };

  const defaultFormValues = mergeWith(
    {},
    formDataToStep2Values,
    defaultStep2Values,
    (formDataAttribute, defaultValue) => (isUndefined(formDataAttribute) ? defaultValue : formDataAttribute),
  );

  const [showWarningDialog, setShowWarningDialog] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<ReportedContentInputs>(defaultFormValues);
  const [inputErrors, setInputErrors] = useState<ReportedContentInputsErrors>({});

  const [assetTypeOptions, setAssetTypeOptions] = useState<
    { contentType: CONTENT_TYPE; commonAssetType: AssetType | undefined }[]
  >([]);
  const [validateUserCountry, setValidateUserCountry] = useState<boolean>(false);

  const { t, i18n } = useTranslation(['infringement_claim_form', 'user_country']);
  const { resolvedEntityType, error: entityTypeError } = useEntityType(formValues.uris);
  const {
    associatedContentElements,
    associatedContent,
    fetchAssociatedContent,
    error: associatedContentError,
  } = useAssociatedContent({ contentType: formValues.contentType });

  const { acceptedUriTypes, maximumNumberOfURI, label: uriFieldLabel } = URI_FIELDS[formValues.contentType!] || {};
  const maxUriAmount = maximumNumberOfURI || getUriMaxAmount(formValues.claimType);
  const areUrisValueValid = areUrisValid(acceptedUriTypes, maxUriAmount);
  const { isFeatureFlagEnabled } = useFeatureFlags();

  const countriesMap = new Map();
  const getValidCountries = () => {
    ALL_COUNTRIES.filter((value: string) => i18n.exists(`user_country_${value}`, { ns: 'user_country' })).map(value =>
      countriesMap.set(t(`user_country_${value}`, { ns: 'user_country' }), value),
    );

    return [...countriesMap.keys()].sort((a, b) => a.localeCompare(b));
  };

  const formatValuesIntoEntities = (uris: string[], associatedContents?: AssociatedContent[]) => {
    if (isEmpty(associatedContent)) {
      return uris.filter((uri: string) => !!uri.length).map((uri: string) => ({ entityId: uri }));
    }

    return formatAssociatedContentIntoEntity(associatedContents || []);
  };

  // All fields on this map is valid because it is a 1<>1 map from the new implementation to the old one
  // All the validation were made on the validation function on this file.
  const mapToFormDataValues = (currentFormValues: ReportedContentInputs) => ({
    [CONTENT_DETAILS.fields.CLAIM_TYPE]: {
      value: currentFormValues.claimType,
      valid: true,
    },
    [CONTENT_DETAILS.fields.REPORTED_CONTENT_TYPE]: {
      value: currentFormValues.contentType,
      valid: true,
    },
    [CONTENT_DETAILS.fields.COMMON_ASSET_TYPE]: {
      value: currentFormValues.commonAssetType ? AssetType[currentFormValues.commonAssetType] : undefined,
      valid: true,
    },
    [CONTENT_DETAILS.fields.REPORTED_CONTENT_DESCRIPTION]: {
      value: currentFormValues.description,
      valid: true,
    },
    [CONTENT_DETAILS.fields.ENTITIES]: {
      value: formatValuesIntoEntities(currentFormValues.uris, currentFormValues.assetIds),
      valid: true,
    },
    [CONTENT_DETAILS.fields.REPORTED_CONTENT_URIS]: {
      value: currentFormValues.uris,
      valid: true,
    },
    [CONTENT_DETAILS.fields.ASSOCIATED_CONTENT]: {
      value: currentFormValues.assetIds,
      valid: true,
    },
    [CONTENT_DETAILS.fields.COUNTRY]: {
      value: currentFormValues.country,
      valid: true,
    },
    [CONTENT_DETAILS.fields.RELEVANT_LAW]: {
      value: currentFormValues.relevantLaw,
      valid: true,
    },
  });

  const handleAssociatedContent = async ({
    reportedUrisValues,
    reportedAssetIds,
  }: {
    reportedUrisValues: string[];
    reportedAssetIds: AssociatedContent[];
  }) => {
    const { associatedContent: newAssociatedContent, associatedContentElements: newAssociatedContentElements } =
      await fetchAssociatedContent(reportedUrisValues);

    if ((isEmpty(newAssociatedContent) || isEmpty(newAssociatedContentElements)) && !isEmpty(reportedAssetIds)) {
      setFormValues(currentFormValues => ({ ...currentFormValues, assetIds: [] }));
    } else {
      const validAssetIds = intersection(reportedAssetIds, newAssociatedContentElements);
      setFormValues(currentFormValues => ({ ...currentFormValues, assetIds: validAssetIds }));
    }
  };

  const onClaimTypeChange = (fieldChange: FieldOnChangeEvent) => {
    const newSelectedClaimType = fieldChange.value as CLAIM_TYPE;
    const newAssetTypeOptions = getAssetTypeOptions(newSelectedClaimType, userCountryIsoCode, isFeatureFlagEnabled);

    setAssetTypeOptions(newAssetTypeOptions);
    setFormValues(currentFormValues => ({
      ...currentFormValues,
      claimType: newSelectedClaimType,
      ...(!!currentFormValues.contentType &&
        currentFormValues.contentType &&
        !newAssetTypeOptions.map(obj => obj.contentType).includes(currentFormValues.contentType) && {
          contentType: undefined,
        }),
      ...(newSelectedClaimType !== CLAIM_TYPE.OTHER && { country: undefined, relevantLaw: undefined }),
    }));

    if (!!formValues.assetIds) {
      handleAssociatedContent({
        reportedUrisValues: formValues.uris,
        reportedAssetIds: formValues.assetIds,
      });
    }
  };

  const validate = (newInputs: ReportedContentInputs): ReportedContentInputsErrors => {
    const newErrors: ReportedContentInputsErrors = {};

    if (some(resolvedEntityType, { entityExists: false }) && newInputs.uris.length === 1) {
      newErrors.uris = true;
    }

    if (!isEmpty(associatedContent) && isEmpty(newInputs.assetIds)) {
      newErrors.assetIds = true;
    }

    return {
      ...newErrors,
      claimType: isEmpty(newInputs.claimType),
      contentType: isEmpty(newInputs.contentType),
      description: isEmpty(newInputs.description),
      uris: isEmpty(newInputs.uris) || !areUrisValueValid(newInputs.uris),
      country: newInputs.claimType === CLAIM_TYPE.OTHER && isEmpty(newInputs.country),
      relevantLaw: newInputs.claimType === CLAIM_TYPE.OTHER && isEmpty(newInputs.relevantLaw),
    };
  };

  const validateFormFieldsAndSubmit = (currentFormValues: ReportedContentInputs) => {
    const formErrors = validate(currentFormValues);
    setValidateUserCountry(true);
    setInputErrors(formErrors);

    const hasFieldsErrors = some(formErrors, Boolean) || !userCountryIsValid;
    let shouldShowWarningDialog = false;

    if (!!currentFormValues.country) {
      currentFormValues.country = countriesMap.get(currentFormValues.country);
    }

    if (!hasFieldsErrors && !isEmpty(associatedContent)) {
      const reportedUrisWithoutAssociatedContentSelected = getUrisWithoutASelectedAssociatedContent(
        currentFormValues.assetIds || [],
        currentFormValues.uris,
        associatedContent,
      );

      shouldShowWarningDialog = !isEmpty(reportedUrisWithoutAssociatedContentSelected);
    }

    if (!hasFieldsErrors && some(resolvedEntityType, { entityExists: false })) {
      const reportedUnexistentUris = filter(
        resolvedEntityType,
        entityType => includes(currentFormValues.uris, entityType.uri) && !entityType.entityExists,
      );

      shouldShowWarningDialog = !isEmpty(reportedUnexistentUris);
    }

    if (associatedContentError || entityTypeError) {
      shouldShowWarningDialog = true;
    }

    setShowWarningDialog(shouldShowWarningDialog);
    const shouldGoToNextStep = !hasFieldsErrors && !shouldShowWarningDialog;

    if (shouldGoToNextStep) {
      goToNextStep([], mapToFormDataValues(currentFormValues));
    }
  };

  const getWarningDialogProps = () => {
    const urisWithoutAnAssociatedContentSelected = getUrisWithoutASelectedAssociatedContent(
      formValues.assetIds || [],
      formValues.uris,
      associatedContent,
    );
    const unexistentUris = filter(resolvedEntityType, ['entityExists', false]).map(({ uri }) => uri);
    const reportedUris = formValues.uris;

    if (!isEmpty(unexistentUris)) {
      return {
        uris: unexistentUris,
        labels: {
          reason: 'not_recognized',
          title: 'resolve_uri_not_found_dialog',
        },
        onClickContinue: () => {
          const existentUris = reportedUris.filter((uri: string) => !unexistentUris.includes(uri));

          const latestFormValues = {
            ...formValues,
            uris: existentUris,
          };

          setFormValues(latestFormValues);
          validateFormFieldsAndSubmit(latestFormValues);
        },
      };
    }

    if (!isEmpty(urisWithoutAnAssociatedContentSelected)) {
      return {
        uris: urisWithoutAnAssociatedContentSelected,
        labels: {
          reason: 'no_content_selected',
          title: 'resolve_uri_not_found_dialog',
        },
        onClickContinue: () => {
          const urisWithSelectedContent = reportedUris.filter(
            (uri: string) => !urisWithoutAnAssociatedContentSelected.includes(uri),
          );

          const latestFormValues = {
            ...formValues,
            uris: urisWithSelectedContent,
          };

          setFormValues(latestFormValues);
          validateFormFieldsAndSubmit(latestFormValues);
        },
      };
    }

    return {
      labels: {
        title: associatedContentError?.message || entityTypeError?.message,
        reason: 'resolve_uri_api_failure_message',
      },
    };
  };

  useEffect(() => {
    if (formData.commonAssetType) {
      setFormValues(currentFormValues => ({
        ...currentFormValues,
        commonAssetType: assetTypeFromJSON(formValues.commonAssetType),
      }));
    }
  }, [formData.commonAssetType, formValues.commonAssetType]);

  useEffect(() => {
    analytics.trackShowStepEvent(ShowStepEventLabel.Step2ReportType);
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {showWarningDialog && (
        <WarningDialog
          onClickClose={() => {
            setShowWarningDialog(false);
          }}
          {...getWarningDialogProps()}
        />
      )}
      <UserCountryField forceValidation={validateUserCountry} />

      <SelectField
        field={{
          name: CONTENT_DETAILS.fields.CLAIM_TYPE,
          type: 'text',
          options: CLAIM_TYPE_OPTIONS,
          required: true,
          labelOverride: USE_LABEL_FROM_TRANSLATION_PREFIX,
        }}
        hasDefaultPlaceholder
        value={formValues.claimType}
        valid={!inputErrors.claimType}
        tPrefix={withFullTranslationKey(CONTENT_DETAILS.translationPrefixes.CLAIM_TYPE)}
        onFieldChange={onClaimTypeChange}
        forceValidation
        errorMessageKey={`${TRANSLATION_PREFIX}:field_required`}
      />

      <SelectField
        field={{
          name: CONTENT_DETAILS.fields.REPORTED_CONTENT_TYPE,
          type: 'text',
          options: assetTypeOptions.map(asset => asset.contentType),
          required: true,
          labelOverride: USE_LABEL_FROM_TRANSLATION_PREFIX,
        }}
        hasDefaultPlaceholder
        value={formValues.contentType}
        valid={!inputErrors.contentType}
        tPrefix={withFullTranslationKey(CONTENT_DETAILS.translationPrefixes.REPORTED_CONTENT_TYPE)}
        onFieldChange={(fieldChangeEvent: FieldOnChangeEvent) => {
          const selectedContentType = fieldChangeEvent.value;
          const selectedAssetType = assetTypeOptions.find(
            asset => asset.contentType === selectedContentType,
          )?.commonAssetType;

          setFormValues(currentFormValues => ({
            ...currentFormValues,
            contentType: selectedContentType,
            commonAssetType: selectedAssetType,
          }));
          if (!!formValues.assetIds) {
            handleAssociatedContent({
              reportedUrisValues: formValues.uris,
              reportedAssetIds: formValues.assetIds,
            });
          }
        }}
        forceValidation
        errorMessageKey={`${TRANSLATION_PREFIX}:field_required`}
      />

      {!!formValues.contentType && (
        <URIField
          label={uriFieldLabel}
          maxURIsAmount={maxUriAmount}
          value={formValues.uris}
          onChange={newReportedUris => {
            setFormValues(currentFormValues => ({ ...currentFormValues, uris: newReportedUris }));
            handleAssociatedContent({
              reportedUrisValues: newReportedUris,
              reportedAssetIds: formValues.assetIds || [],
            });
          }}
          isFieldValid={areUrisValueValid}
          translationPrefix="infringement_claim_form"
          key={formValues.contentType}
          errorMessage={
            formValues.uris.length === 1 ? getURIErrorMessage(resolvedEntityType[0], associatedContent[0]) : undefined
          }
        />
      )}

      {!isEmpty(associatedContentElements) && (
        <ImageChooser
          field={{
            name: CONTENT_DETAILS.fields.ASSOCIATED_CONTENT,
            required: true,
          }}
          valid={!inputErrors.assetIds}
          onFieldChange={(fieldChange: FieldOnChangeEvent) => {
            setFormValues(currentFormValues => ({
              ...currentFormValues,
              assetIds: fieldChange.value,
            }));
          }}
          value={formValues.assetIds || []}
          label={t(`${TRANSLATION_PREFIX}:image_chooser_title`)}
          translations={{
            errorMessage: t(`${TRANSLATION_PREFIX}:image_chooser_title`),
            disclaimer: t(`${TRANSLATION_PREFIX}:form_associated_content_disclaimer`),
            blurWarning: t(`${TRANSLATION_PREFIX}:image_unblur_warning`),
            pagination: t(`${TRANSLATION_PREFIX}:pagination_of`),
          }}
          images={associatedContentElements}
          imageProperty="value"
        />
      )}

      {formValues.claimType === CLAIM_TYPE.OTHER && (
        <>
          <SelectField
            field={{
              name: CONTENT_DETAILS.fields.COUNTRY,
              type: 'text',
              options: getValidCountries(),
              required: true,
              labelOverride: USE_LABEL_FROM_TRANSLATION_PREFIX,
            }}
            hasDefaultPlaceholder
            showRawOptionLabels
            value={formValues.country}
            valid={!inputErrors.country}
            tPrefix={withFullTranslationKey(CONTENT_DETAILS.translationPrefixes.COUNTRY)}
            onFieldChange={(fieldChangeEvent: FieldOnChangeEvent) => {
              setFormValues(currentFormValues => ({
                ...currentFormValues,
                country: fieldChangeEvent.value,
              }));
            }}
            forceValidation
            errorMessageKey={`${TRANSLATION_PREFIX}:field_required`}
          />
          <TextField
            field={{
              name: CONTENT_DETAILS.fields.RELEVANT_LAW,
              type: 'text',
              required: true,
              maxlength: 200,
              labelOverride: t(CONTENT_DETAILS.labels.RELEVANT_LAW),
            }}
            value={formValues.relevantLaw}
            valid={!inputErrors.relevantLaw}
            onFieldChange={(fieldChangeEvent: FieldOnChangeEvent) => {
              setFormValues(currentFormValues => ({
                ...currentFormValues,
                relevantLaw: fieldChangeEvent.value,
              }));
            }}
            forceValidation
            errorMessageKey={`${TRANSLATION_PREFIX}:field_required`}
          />
        </>
      )}

      <TextareaField
        field={{
          name: CONTENT_DETAILS.fields.REPORTED_CONTENT_DESCRIPTION,
          type: 'text',
          required: true,
          maxlength: 2000,
          labelOverride: t(getContentDescriptionLabel(formValues.claimType)),
        }}
        value={formValues.description}
        valid={!inputErrors.description}
        onFieldChange={(fieldChangeEvent: FieldOnChangeEvent) =>
          setFormValues(currentFormValues => ({
            ...currentFormValues,
            description: fieldChangeEvent.value,
          }))
        }
        forceValidation
        errorMessageKey={`${TRANSLATION_PREFIX}:field_required`}
      />

      {renderErrorMessage()}
      <BackButton handleBackButton={handleBackButton} />
      <ButtonPrimary onClick={() => validateFormFieldsAndSubmit(formValues)}>{t('continue')}</ButtonPrimary>
    </>
  );
};

export default Step2ReportedContent;
